import { useAtomValue } from 'jotai';
import { LockIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { cn, TableRow } from '@crossnokaye/ux-common';

import { Sequence, SequenceItem } from '@repo/sdk/models/components';

import { AddStageColumnCell } from '@/components/sequencer-table/add-stage-column';
import { EquipmentCell } from '@/components/sequencer-table/equipment-cell';
import {
  addStageToSequence,
  cycleStageInSequence,
  enableRowInSequence,
  getNestedValue,
  removeRowFromSequence,
  removeStageFromSequence,
} from '@/components/sequencer-table/helpers';
import { SequenceCell } from '@/components/sequencer-table/sequence-cell';
import { SequenceEditorTable } from '@/components/sequencer-table/sequence-editor-table';
import {
  currentStageValueAtom,
  SequenceWithValues,
} from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import {
  CondenserSequencerStage,
  cycleCondenserSequencerStageValue,
  deriveCondenserSequencerIcon,
  getCondenserSequencerCellStyles,
  getCondenserSequencerOverlayProperties,
  getCondenserSequenceState,
} from './helpers';
import { SequenceActivityPill } from '../components/sequence-activity-pill';
import { SequencerLegend } from '../components/sequencer-legend';
import { activeStageLegendItem, standardLegendItems } from '../components/sequencer-legend-items';
import { SequencerSectionTemplate } from '../components/sequencer-section-template';

const condenserLegendItems = [...standardLegendItems, activeStageLegendItem];

interface CondenserSequencerEditorProps {
  isActive?: boolean;
  sequenceWithValues: SequenceWithValues;
  /** Whether to show the activity pill in the header. Only used for V2 sequencers with multiple sequences. */
  showActivityPill?: boolean;
}

const getStageValue = (
  sequence: Sequence | undefined,
  rowIndex: number,
  columnIndex: number
): number => {
  return sequence?.table?.[rowIndex]?.stageValues?.[columnIndex] || 0;
};

/**
 * Returns the content of the cell for a condenser sequencer stage.
 *
 * @param stageValue - The stage value to get the cell content for.
 * @returns The content of the cell for a condenser sequencer stage.
 */
const getCondenserSequencerCellContent = (stageValue: number): React.ReactNode => {
  switch (stageValue) {
    case CondenserSequencerStage.UNAVAILABLE:
      return <LockIcon className={cn('w-4 h-4 stroke-muted-foreground')} />;
  }
  return null;
};

export const CondenserSequencerEditor = ({
  isActive,
  sequenceWithValues,
  showActivityPill = false,
}: CondenserSequencerEditorProps) => {
  const form = useFormContext();
  const activeStage = useAtomValue(currentStageValueAtom);
  const { isDisabled: disabledByPermissions, tooltipText: disabledTooltipText } =
    useActionPermission({
      privilege: PRIVILEGES.writeCommands,
    });

  // Get the form field key from the ref ID
  const formFieldKey = sequenceWithValues.ref?.id ? `sequencer.${sequenceWithValues.ref.id}` : '';

  // Get the current sequence value from the form
  const sequenceValue = (formFieldKey ? form.watch(formFieldKey) : undefined) as
    | Sequence
    | undefined;

  const handleRemoveRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeRowFromSequence(sequence, rowIndex);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = enableRowInSequence(sequence, rowIndex, CondenserSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = addStageToSequence(sequence, CondenserSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleRemoveStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeStageFromSequence(sequence);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleStageClicked = (
    sequence: Sequence | undefined,
    rowIndex: number,
    columnIndex: number
  ) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = cycleStageInSequence(
      sequence,
      rowIndex,
      columnIndex,
      cycleCondenserSequencerStageValue
    );
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const maxStages = Math.max(
    ...(sequenceValue?.table?.map((seq: any) => seq.stageValues.length - 1) || [])
  );
  const columnTitles = Array.from({ length: maxStages }, (_, i) => `Stage ${i + 1}`);
  const rowTitles = sequenceValue?.table?.map((item: SequenceItem) => item.name) || [];

  const overlayProperties = getCondenserSequencerOverlayProperties(sequenceValue);
  const defaultValues = form?.formState?.defaultValues;

  const originalSequence = defaultValues
    ? (getNestedValue(defaultValues, formFieldKey) as Sequence)
    : undefined;

  return (
    <SequencerSectionTemplate
      legend={<SequencerLegend items={condenserLegendItems} />}
      icon={showActivityPill ? <SequenceActivityPill isActive={isActive ?? false} /> : undefined}
      title={sequenceWithValues.label}
      onAddStage={() => handleAddStageClicked(sequenceValue)}
    >
      <SequenceEditorTable
        activeStage={typeof activeStage === 'number' ? activeStage : undefined}
        onAddStageClicked={() => handleAddStageClicked(sequenceValue)}
        onRemoveStageClicked={() => {
          handleRemoveStageClicked(sequenceValue);
        }}
        overlayProperties={overlayProperties}
        sequence={sequenceValue}
      >
        {rowTitles.map((row: string, rowIndex: number): React.ReactNode => {
          const isRowDisabled =
            sequenceValue?.table?.[rowIndex]?.stageValues?.every(
              (stageValue: number) => stageValue === 0
            ) ?? true;

          return (
            <TableRow key={`${row}_row_${rowIndex}`}>
              <EquipmentCell
                icon={deriveCondenserSequencerIcon(row)}
                isRowDisabled={isRowDisabled}
                onAddRow={() => handleAddRowClicked(sequenceValue, rowIndex)}
                onRemoveRow={() => handleRemoveRowClicked(sequenceValue, rowIndex)}
                rowTitle={rowTitles[rowIndex]}
              />
              {columnTitles.map((column: string, columnIndex: number) => {
                // Adjusted column index because the table in the sequencer always
                // has zero at the first index (all devices off).
                const adjustedColumnIndex = columnIndex + 1;

                // Get the stage value for the current cell.
                const stageValue = getStageValue(sequenceValue, rowIndex, adjustedColumnIndex);

                // Get the styles for a Cell in a Condenser Sequencer, based on the stage.
                const { className, style } = getCondenserSequencerCellStyles(stageValue);

                // Calculate if this cell is dirty by comparing with original value
                const originalValue = getStageValue(
                  originalSequence,
                  rowIndex,
                  adjustedColumnIndex
                );
                const isDirty = originalValue !== stageValue;

                // Only highlight active column if this sequence is the active one
                const isActiveColumn = isActive && activeStage === adjustedColumnIndex;
                const tooltipContent = getCondenserSequenceState(stageValue);
                const isUnavailable = stageValue === 0;
                return (
                  <SequenceCell
                    className={className}
                    disabledTooltipText={disabledTooltipText}
                    isColumnActive={isActiveColumn ?? false}
                    isDisabled={disabledByPermissions}
                    isDirty={isDirty}
                    isUnavailable={isUnavailable}
                    key={`${column}_cell_${row}_row_${rowIndex}_column_${adjustedColumnIndex}`}
                    onClick={() => {
                      handleStageClicked(sequenceValue, rowIndex, adjustedColumnIndex);
                    }}
                    original={
                      originalValue !== undefined
                        ? getCondenserSequenceState(originalValue)
                        : undefined
                    }
                    style={style}
                    tooltipContent={tooltipContent}
                  >
                    {getCondenserSequencerCellContent(stageValue)}
                  </SequenceCell>
                );
              })}
              <AddStageColumnCell />
            </TableRow>
          );
        })}
      </SequenceEditorTable>
    </SequencerSectionTemplate>
  );
};
